import { type FocusEvent, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import { Button } from '@/ui/button'
import { buttonVariants } from '@/ui/button-variants'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { Tabs, TabsList, TabsTab, tabsTabClassName } from '@/ui/tabs'

import type { DataGridSavedView } from '../api/data-grid-saved-views-api'
import type { DataGridSavedViewsState } from '../lib/use-data-grid-saved-views'
import { useGridContext } from './data-grid-context'

const EVERYTHING = 'everything'
const MORE = 'more'
const MODIFIED = 'modified'
const TAB_GAP = 4
const BADGE_GAP = 6
const NAME_CLASS_NAME = 'max-w-48 min-w-0 truncate'
const BADGE_CLASS_NAME = 'bg-muted text-foreground shrink-0 rounded-sm px-1 text-[10px] leading-4 font-medium'
const MORE_CLASS_NAME = cn(buttonVariants({ variant: 'ghost' }), 'text-muted-foreground')

type Measures = { available: number; widths: Readonly<Record<string, number>> }

type SaveError = { savedViewId: string; message: string }

export function DataGridSavedViewTabs() {
  const { grid } = useGridContext()
  if (!grid.savedViews) return null
  return <SavedViewTabStrip key={grid.savedViews.scopeKey} savedViews={grid.savedViews} />
}

function SavedViewTabStrip({ savedViews }: { savedViews: DataGridSavedViewsState }) {
  const announce = useAnnounce()
  const views = savedViews.views ?? []
  const origin = savedViews.originView
  const overwrite = savedViews.overwrite
  const modified = savedViews.status === 'custom' && origin !== null && !savedViews.isApplying
  const selected = savedViews.awaitingDefault
    ? null
    : savedViews.activeView?.savedViewId
      ?? (savedViews.status === 'everything' ? EVERYTHING : origin?.savedViewId ?? null)
  const [saveError, setSaveError] = useState<SaveError | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const pickedFromMoreRef = useRef(false)
  const keepFocusOffMoreRef = useRef(false)
  const { containerRef, measurerRef, measures } = useTabMeasures(JSON.stringify(views.map((view) => view.name)))
  const { shown, hidden } = fitTabs(views, measures, selected, modified)
  const listKey = shown.map((view) => view.savedViewId).join()
  const moreShown = hidden.length > 0
  const keepFocus = useFocusAcrossRemounts(listRef, listKey)
  const listFailed = savedViews.query.isError
  const shownError = saveError !== null && (
    (modified && origin.savedViewId === saveError.savedViewId)
    || !views.some((view) => view.savedViewId === saveError.savedViewId)
  ) ? saveError.message : null
  if (saveError !== null && shownError === null) setSaveError(null)

  useEffect(() => {
    if (listFailed) announce('Saved views couldn’t be loaded.')
  }, [announce, listFailed])

  useLayoutEffect(() => {
    if (!pickedFromMoreRef.current || moreShown) return
    const frame = requestAnimationFrame(focusTabAfterPick)
    return () => cancelAnimationFrame(frame)
  }, [moreShown])

  function choose(value: string) {
    setSaveError(null)
    if (value === EVERYTHING) {
      savedViews.apply(null)
      return
    }
    const view = views.find((candidate) => candidate.savedViewId === value)
    if (view) savedViews.apply(view)
  }

  function chooseFromMore(value: string) {
    pickedFromMoreRef.current = true
    keepFocusOffMoreRef.current = true
    choose(value)
  }

  function returnFocusToMore() {
    const returnToMore = !keepFocusOffMoreRef.current
    keepFocusOffMoreRef.current = false
    return returnToMore
  }

  function focusTabAfterPick() {
    if (!pickedFromMoreRef.current) return
    pickedFromMoreRef.current = false
    focusSelectedTab(listRef.current)
  }

  function save(view: DataGridSavedView) {
    focusSelectedTab(listRef.current)
    setSaveError(null)
    overwrite.mutate({ savedViewId: view.savedViewId }, {
      onSuccess: () => announce(`Saved changes to “${view.name}”`),
      onError: (error) => setSaveError({ savedViewId: view.savedViewId, message: overwrite.errorMessage(error) }),
    })
  }

  function revert(view: DataGridSavedView) {
    focusSelectedTab(listRef.current)
    setSaveError(null)
    savedViews.apply(view)
    announce(`Reverted to “${view.name}”`)
  }

  if (savedViews.query.isSuccess && views.length === 0 && shownError === null) return null

  return (
    <div data-slot="data-grid-saved-view-tabs" className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 px-4 pt-2 pb-1">
      <div ref={containerRef} className="relative flex min-w-0 grow basis-72 items-center gap-1 overflow-hidden p-0.5">
        <Tabs
          value={selected}
          onValueChange={(value, details) => {
            if (details.reason === 'none') choose(String(value))
          }}
        >
          <TabsList
            key={listKey}
            ref={listRef}
            aria-label="Saved views"
            aria-busy={savedViews.query.isPending || savedViews.awaitingDefault || undefined}
            className="min-w-0"
            {...keepFocus}
          >
            <TabsTab value={EVERYTHING}>Everything</TabsTab>
            {shown.map((view) => {
              const isModified = modified && view.savedViewId === origin.savedViewId
              const label = isModified ? `${view.name}, modified` : view.name
              return (
                <TabsTab
                  key={view.savedViewId}
                  value={view.savedViewId}
                  title={label}
                  aria-label={isModified ? label : undefined}
                  className={view.savedViewId === selected ? 'min-w-0 shrink' : undefined}
                >
                  <span className={NAME_CLASS_NAME}>{view.name}</span>
                  {isModified ? <span className={BADGE_CLASS_NAME}>Modified</span> : null}
                </TabsTab>
              )
            })}
          </TabsList>
        </Tabs>
        {moreShown ? (
          <DropdownMenu onOpenChangeComplete={(open) => { if (!open) focusTabAfterPick() }}>
            <DropdownMenuTrigger aria-label="More views" className={MORE_CLASS_NAME}>
              More
              <ChevronDown aria-hidden className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" finalFocus={returnFocusToMore}>
              <DropdownMenuRadioGroup aria-label="More views" value={selected ?? ''} onValueChange={chooseFromMore}>
                {hidden.map((view) => (
                  <DropdownMenuRadioItem key={view.savedViewId} value={view.savedViewId} indicator="check" closeOnClick>
                    <span className={NAME_CLASS_NAME}>{view.name}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        <div aria-hidden inert className="pointer-events-none invisible absolute top-0 left-0 size-0 overflow-hidden">
          <div ref={measurerRef} className="flex w-max">
            <span data-measure={EVERYTHING} className={tabsTabClassName}>Everything</span>
            {views.map((view) => (
              <span key={view.savedViewId} data-measure={view.savedViewId} className={tabsTabClassName}>
                <span className={NAME_CLASS_NAME}>{view.name}</span>
              </span>
            ))}
            <span data-measure={MODIFIED} className={BADGE_CLASS_NAME}>Modified</span>
            <span data-measure={MORE} className={MORE_CLASS_NAME}>
              More
              <ChevronDown className="size-3.5" />
            </span>
          </div>
        </div>
      </div>
      {listFailed ? (
        <Button type="button" variant="ghost" onClick={() => void savedViews.query.refetch()}>
          Retry saved views
        </Button>
      ) : null}
      {modified ? (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            disabled={!savedViews.canOverwrite || overwrite.isPending}
            aria-label={overwrite.isPending ? undefined : `Save changes to “${origin.name}”`}
            onClick={() => save(origin)}
          >
            {overwrite.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={overwrite.isPending}
            aria-label={`Revert to “${origin.name}”`}
            onClick={() => revert(origin)}
          >
            Revert
          </Button>
        </div>
      ) : null}
      {shownError ? <p role="alert" className="text-destructive basis-full text-xs">{shownError}</p> : null}
    </div>
  )
}

function selectedTab(list: HTMLElement | null) {
  return list?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    ?? list?.querySelector<HTMLElement>('[role="tab"]')
    ?? null
}

function focusSelectedTab(list: HTMLElement | null) {
  selectedTab(list)?.focus()
}

/**
 * The tab list remounts whenever the tabs that fit change, which drops focus from a tab that was removed. When focus
 * was in the list, it returns to the selected tab, or Everything when none is selected.
 */
function useFocusAcrossRemounts(listRef: RefObject<HTMLDivElement | null>, listKey: string) {
  const focusedRef = useRef(false)

  useLayoutEffect(() => {
    const list = listRef.current
    if (focusedRef.current && list && !list.contains(document.activeElement)) focusSelectedTab(list)
  }, [listKey, listRef])

  return {
    onFocus: () => { focusedRef.current = true },
    onBlur: (event: FocusEvent<HTMLDivElement>) => {
      if (event.relatedTarget instanceof Node) {
        focusedRef.current = event.currentTarget.contains(event.relatedTarget)
        return
      }
      requestAnimationFrame(() => {
        if (!listRef.current?.contains(document.activeElement)) focusedRef.current = false
      })
    },
  }
}

function useTabMeasures(labelsKey: string) {
  const containerRef = useRef<HTMLDivElement>(null)
  const measurerRef = useRef<HTMLDivElement>(null)
  const [measures, setMeasures] = useState<Measures>({ available: 0, widths: {} })

  useLayoutEffect(() => {
    const container = containerRef.current
    const measurer = measurerRef.current
    if (!container || !measurer) return
    function measure() {
      if (!container || !measurer) return
      const style = getComputedStyle(container)
      const next: Measures = {
        available: container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
        widths: Object.fromEntries([...measurer.querySelectorAll<HTMLElement>('[data-measure]')]
          .map((element) => [element.dataset.measure, element.getBoundingClientRect().width])),
      }
      setMeasures((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    observer.observe(measurer)
    return () => observer.disconnect()
  }, [labelsKey])

  return { containerRef, measurerRef, measures }
}

/** Splits the views into the tabs that fit and the rest, for the More menu. The selected tab always stays. */
function fitTabs(
  views: readonly DataGridSavedView[],
  { available, widths }: Measures,
  selected: string | null,
  modified: boolean,
): { shown: DataGridSavedView[]; hidden: DataGridSavedView[] } {
  if (!(available > 0)) return { shown: [...views], hidden: [] }
  const width = (id: string) => (widths[id] ?? 0) + TAB_GAP
    + (modified && id === selected ? (widths[MODIFIED] ?? 0) + BADGE_GAP : 0)
  const everything = widths[EVERYTHING] ?? 0
  const total = views.reduce((sum, view) => sum + width(view.savedViewId), everything)
  if (total <= available) return { shown: [...views], hidden: [] }

  const pinned = views.find((view) => view.savedViewId === selected)
  let room = available - everything - TAB_GAP - (widths[MORE] ?? 0) - (pinned ? width(pinned.savedViewId) : 0)
  const kept = new Set(pinned ? [pinned.savedViewId] : [])
  for (const view of views) {
    if (view === pinned) continue
    const needed = width(view.savedViewId)
    if (needed > room) break
    room -= needed
    kept.add(view.savedViewId)
  }
  return {
    shown: views.filter((view) => kept.has(view.savedViewId)),
    hidden: views.filter((view) => !kept.has(view.savedViewId)),
  }
}
